import type { AppStore } from "@/store/AppStore";
import { CreateAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/CreateAudioRecordingOperation";
import { UpdateAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/UpdateAudioRecordingOperation";
import { DeleteAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/DeleteAudioRecordingOperation";
import { TrashAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/TrashAudioRecordingOperation";
import { RestoreAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/RestoreAudioRecordingOperation";
import { MarkStartedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedLocalAudioRecordingOperation";
import { MarkCompletedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedLocalAudioRecordingOperation";
import { MarkErrorStartingLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorStartingLocalAudioRecordingOperation";
import { MarkErrorResumingLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorResumingLocalAudioRecordingOperation";
import { MarkPausedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkPausedLocalAudioRecordingOperation";
import { MarkResumedLocalAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkResumedLocalAudioRecordingOperation";
import { MarkStartedUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedUploadingAudioRecordingOperation";
import { MarkCompletedUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedUploadingAudioRecordingOperation";
import { MarkSkippedUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkSkippedUploadingAudioRecordingOperation";
import { MarkErrorUploadingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorUploadingAudioRecordingOperation";
import { MarkTranscriptionDisconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkTranscriptionDisconnectedAudioRecordingOperation";
import { MarkTranscriptionReconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkTranscriptionReconnectedAudioRecordingOperation";
import { MarkTranscriptionCompromisedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkTranscriptionCompromisedAudioRecordingOperation";
import { MarkVoiceDisconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkVoiceDisconnectedAudioRecordingOperation";
import { MarkVoiceReconnectedAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkVoiceReconnectedAudioRecordingOperation";
import { MarkStartedProcessingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedProcessingAudioRecordingOperation";
import { MarkErrorProcessingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorProcessingAudioRecordingOperation";
import { MarkCompletedProcessingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedProcessingAudioRecordingOperation";
import { MarkStartedRetranscribingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkStartedRetranscribingAudioRecordingOperation";
import { MarkCompletedRetranscribingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkCompletedRetranscribingAudioRecordingOperation";
import { MarkErrorRetranscribingAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkErrorRetranscribingAudioRecordingOperation";
import { MarkUnexpectedErrorAudioRecordingOperation } from "@/store/sync/operations/audio-recordings/MarkUnexpectedErrorAudioRecordingOperation";
import {
  AudioRecordingObservable,
  AudioRecordingProcessingStatus,
  AudioRecordingRetranscriptionStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingStrategy,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";
import type { Uuid } from "@/domains/global/identifiers";
import type { AudioUploadFailureDebugInfo } from "@mem-client/uploads";
import { AudioUploadFailureStage } from "@mem-client/uploads";

import { BaseSyncModelRepository } from "../base-repository";
import { LocalAudioRecordingBlobsRepository } from "./local-audio-recording-blobs";
import { LocalAudioRecordingTranscriptTurnsRepository } from "./local-audio-recording-transcript-turns";
import {
  buildRecordingCompletedAppEventProperties,
  buildUploadErrorAppEventProperties,
  buildUploadStartAppEventProperties,
  compactNullish,
  extractPersistedCompletionAppEventProperties,
  type RecordingCompletedArtifactSummary,
  type RecordingCompletedSilenceSummary,
} from "./audio-recording-app-event-properties";
import { uuidModule } from "@/modules/uuid";
import { clientEnvModule } from "@/modules/client-env";
import { AudioRecordingRecord } from "@/domains/dexie-db/models/audio-recording/audio-recording";
import type { IAudioRecordingAppEventOperation } from "@/store/sync/operations/types";
import type { ICreateAudioRecordingOperation } from "@/store/sync/operations/types";
import { WebAudioArtifactService } from "@/modules/audio-recording/web-artifacts";
import { MEETING_HANDOFF_PREVIOUS_AUDIO_RECORDING_ID_PROPERTY } from "@/store/audio-recordings/app-events";

type RepositoryDependencies = {
  localAudioRecordingBlobs: LocalAudioRecordingBlobsRepository;
  localAudioRecordingTranscriptTurns: LocalAudioRecordingTranscriptTurnsRepository;
  webAudioArtifacts: WebAudioArtifactService;
};

type UpdateAudioRecordingData = {
  associatedNoteId?: Uuid | null;
  recordingStatus?: AudioRecordingRecordingStatus;
  uploadStatus?: AudioRecordingUploadStatus;
  processingStatus?: AudioRecordingProcessingStatus;
  retranscriptionStatus?: AudioRecordingRetranscriptionStatus;
  recordingStrategy?: AudioRecordingStrategy;
};

export class AudioRecordingRepository extends BaseSyncModelRepository<
  AudioRecordingRecord,
  AudioRecordingObservable
> {
  private readonly store: AppStore;
  private readonly repositories: RepositoryDependencies;

  constructor({ store, repositories }: { store: AppStore; repositories: RepositoryDependencies }) {
    super({ store });
    this.store = store;
    this.repositories = repositories;
  }

  async upsert({
    id,
    data,
  }: {
    id: Uuid;
    data: UpdateAudioRecordingData;
  }): Promise<Pick<AudioRecordingObservable, "id">> {
    const existing = await this.getById({ id });

    if (existing) {
      return { id };
    }

    return this.create({ id, data });
  }

  async create({
    id,
    data,
  }: {
    id?: Uuid | null;
    data: UpdateAudioRecordingData;
  }): Promise<Pick<AudioRecordingObservable, "id">> {
    const audioRecordingId = id ?? uuidModule.generate();
    const uploadStatus = (data.uploadStatus ?? AudioRecordingUploadStatus.INITIAL) as NonNullable<
      ICreateAudioRecordingOperation["payload"]["upload_status"]
    >;

    /**
     * Note that we specifically use the client group ID here, rather than the client ID.
     * This is because audio-recordings matter per-device, rather than per-tab.
     */
    const { clientGroupId } = await clientEnvModule.clientGroupId();

    await new CreateAudioRecordingOperation({
      store: this.store,
      payload: {
        id: audioRecordingId,
        recording_status: data.recordingStatus ?? AudioRecordingRecordingStatus.INITIAL,
        upload_status: uploadStatus,
        processing_status: data.processingStatus ?? AudioRecordingProcessingStatus.INITIAL,
        retranscription_status:
          data.retranscriptionStatus ?? AudioRecordingRetranscriptionStatus.INITIAL,
        associated_note_id: data.associatedNoteId ?? null,
        associated_client_id: clientGroupId,
        associated_platform_kind: clientEnvModule.platformKind(),
        value_json: {
          source_kind: data.recordingStrategy ?? AudioRecordingStrategy.WebArtifact,
        },
      },
    }).execute();

    return {
      id: audioRecordingId,
    };
  }

  async markAsRecordingStarted({
    id,
    recallSdkUploadId,
    handoffPreviousAudioRecordingId,
  }: {
    id: Uuid;
    recallSdkUploadId?: string | null;
    handoffPreviousAudioRecordingId?: Uuid | null;
  }): Promise<void> {
    await new MarkStartedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, {
        recall_sdk_upload_id: recallSdkUploadId,
        [MEETING_HANDOFF_PREVIOUS_AUDIO_RECORDING_ID_PROPERTY]: handoffPreviousAudioRecordingId,
      }),
    }).execute();
  }

  async markAsRecordingCompleted({
    id,
    durationMs,
    recordingStrategy,
    transcriptTurnCount,
    artifactSummary,
    silenceSnapshot,
    deferredPipelineSuccessorAudioRecordingId,
  }: {
    id: Uuid;
    durationMs?: number | null;
    recordingStrategy?: AudioRecordingStrategy | null;
    transcriptTurnCount?: number | null;
    artifactSummary?: RecordingCompletedArtifactSummary;
    silenceSnapshot?: RecordingCompletedSilenceSummary | null;
    deferredPipelineSuccessorAudioRecordingId?: Uuid | null;
  }): Promise<void> {
    const occurredAt = new Date().toISOString();
    const [existing, { clientGroupId }, clientId] = await Promise.all([
      this.getById({ id }),
      clientEnvModule.clientGroupId(),
      clientEnvModule.clientId(),
    ]);

    await new MarkCompletedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(
        id,
        buildRecordingCompletedAppEventProperties({
          audioRecording: existing,
          durationMs,
          recordingStrategy,
          transcriptTurnCount,
          artifactSummary,
          silenceSnapshot,
          deferredPipelineSuccessorAudioRecordingId,
          clientGroupId,
          clientId,
          occurredAt,
        }),
        occurredAt
      ),
    }).execute();
  }

  async markAsRecordingErrored({
    id,
    errorMessage,
    errorName,
    errorSource,
    errorStage,
    errorSummary,
    errorDetails,
    failureReason,
    recordingStrategy,
    forceWebMediaController,
    wasPaused,
  }: {
    id: Uuid;
    errorMessage?: string | null;
    errorName?: string | null;
    errorSource?: string | null;
    errorStage?: string | null;
    errorSummary?: string | null;
    errorDetails?: Record<string, unknown> | null;
    failureReason?: string | null;
    recordingStrategy?: AudioRecordingStrategy | null;
    forceWebMediaController?: boolean | null;
    wasPaused?: boolean | null;
  }): Promise<void> {
    await new MarkErrorStartingLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, {
        error_message: errorMessage,
        error_name: errorName,
        error_source: errorSource,
        error_stage: errorStage,
        error_summary: errorSummary,
        error_details: errorDetails ?? undefined,
        failure_reason: failureReason,
        recording_strategy: recordingStrategy,
        force_web_media_controller: forceWebMediaController,
        was_paused: wasPaused,
      }),
    }).execute();
  }

  async markAsPaused({ id }: { id: Uuid }): Promise<void> {
    await new MarkPausedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsResumed({
    id,
    recallSdkUploadId,
  }: {
    id: Uuid;
    recallSdkUploadId?: string | null;
  }): Promise<void> {
    await new MarkResumedLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { recall_sdk_upload_id: recallSdkUploadId }),
    }).execute();
  }

  async markAsResumingErrored({
    id,
    errorMessage,
  }: {
    id: Uuid;
    errorMessage?: string | null;
  }): Promise<void> {
    await new MarkErrorResumingLocalAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, { error_message: errorMessage }),
    }).execute();
  }

  async markAsUploading({ id, forceRestart }: { id: Uuid; forceRestart?: boolean }): Promise<void> {
    const existing = await this.getById({ id });

    await new MarkStartedUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(
        id,
        buildUploadStartAppEventProperties({
          audioRecording: existing,
          forceRestart,
        })
      ),
    }).execute();
  }

  async markAsUploadCompleted({ id }: { id: Uuid }): Promise<void> {
    await new MarkCompletedUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsUploadSkipped({ id }: { id: Uuid }): Promise<void> {
    await new MarkSkippedUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsUploadErrored({
    id,
    errorMessage,
    uploadStage,
    forceRestart,
    debugInfo,
    failureKind,
  }: {
    id: Uuid;
    errorMessage?: string | null;
    uploadStage?: AudioUploadFailureStage;
    forceRestart?: boolean;
    debugInfo?: AudioUploadFailureDebugInfo;
    failureKind?: "artifact-missing" | "upload-failed";
  }): Promise<void> {
    const existing = await this.getById({ id });

    await new MarkErrorUploadingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id, {
        ...extractPersistedCompletionAppEventProperties({ audioRecording: existing }),
        ...buildUploadErrorAppEventProperties({
          errorMessage,
          uploadStage,
          forceRestart,
          debugInfo,
          failureKind,
        }),
      }),
    }).execute();
  }

  async markTranscriptionDisconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkTranscriptionDisconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markTranscriptionReconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkTranscriptionReconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markTranscriptionCompromised({ id }: { id: Uuid }): Promise<void> {
    await new MarkTranscriptionCompromisedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markVoiceDisconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkVoiceDisconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markVoiceReconnected({ id }: { id: Uuid }): Promise<void> {
    await new MarkVoiceReconnectedAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  private buildAppEventPayload(
    id: Uuid,
    properties?: Record<string, unknown>,
    occurredAt = new Date().toISOString()
  ): IAudioRecordingAppEventOperation["payload"] {
    const compacted = properties ? compactNullish(properties) : undefined;
    const hasProperties = compacted !== undefined && Object.keys(compacted).length > 0;

    return {
      schema_version: 1,
      id,
      occurred_at: occurredAt,
      properties: hasProperties ? compacted : undefined,
    };
  }

  async markAsProcessingInProgress({ id }: { id: Uuid }): Promise<void> {
    await new MarkStartedProcessingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  /**
   * Ideally this is set by the server, but there are cases where we know it is completed client-side
   * and can set it here.
   *
   * (eg. after the user accepts a voice suggestion for an in-progress recording.)
   */
  async markAsProcessingCompleted({ id }: { id: Uuid }): Promise<void> {
    await new MarkCompletedProcessingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsProcessingErrored({ id }: { id: Uuid }): Promise<void> {
    await new MarkErrorProcessingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsRetranscribingStarted({ id }: { id: Uuid }): Promise<void> {
    await new MarkStartedRetranscribingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsRetranscribingCompleted({ id }: { id: Uuid }): Promise<void> {
    await new MarkCompletedRetranscribingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsRetranscribingErrored({ id }: { id: Uuid }): Promise<void> {
    await new MarkErrorRetranscribingAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  async markAsUnexpectedError({ id }: { id: Uuid }): Promise<void> {
    await new MarkUnexpectedErrorAudioRecordingOperation({
      store: this.store,
      payload: this.buildAppEventPayload(id),
    }).execute();
  }

  /**
   * Re-associates a local recording with the current device client group.
   *
   * CLIENT_RESET_REQUIRED creates a new client group for the same physical
   * install. When we recover a pre-reset recording from local artifacts, this
   * normal sync operation makes the backend row durable under the new group
   * instead of relying on a local-only alias forever.
   */
  async updateAssociatedClientId({
    id,
    associatedClientId,
  }: {
    id: Uuid;
    associatedClientId: Uuid | null;
  }): Promise<void> {
    await new UpdateAudioRecordingOperation({
      store: this.store,
      payload: {
        id,
        associated_client_id: associatedClientId,
      },
    }).execute();
  }

  async delete({ id }: { id: Uuid }): Promise<void> {
    await new DeleteAudioRecordingOperation({
      store: this.store,
      payload: { id },
    }).execute();

    await Promise.all([
      this.repositories.localAudioRecordingBlobs.deleteAllByRecordingId({
        associatedAudioRecordingId: id,
      }),
      this.repositories.localAudioRecordingTranscriptTurns.deleteAllByRecordingId({
        associatedAudioRecordingId: id,
      }),
      this.repositories.webAudioArtifacts.removeAudioArtifact({
        audioRecordingId: id,
      }),
    ]);
  }

  async getById({ id }: { id: Uuid }): Promise<AudioRecordingRecord | null> {
    const localModel = await this.store.audioRecordings.localTable.get(id);
    if (!localModel) return null;

    return {
      id,
      data: localModel.model_data,
    };
  }

  async trash({ id }: { id: Uuid }): Promise<void> {
    await new TrashAudioRecordingOperation({
      store: this.store,
      payload: { id },
    }).execute();
  }

  async restore({ id }: { id: Uuid }): Promise<void> {
    await new RestoreAudioRecordingOperation({
      store: this.store,
      payload: { id },
    }).execute();
  }

  async hasAnyForNote({ noteId }: { noteId: Uuid }): Promise<boolean> {
    const table = this.store.audioRecordings.localTable;
    const { clientGroupId } = await clientEnvModule.clientGroupId();

    const latestRecording = await table
      .where("associated_note_id")
      .equals(noteId)
      .filter(row => row.trashed_at === null)
      .filter(row => row.associated_client_id === clientGroupId)
      .sortBy("locally_created_at")
      .then(records => records.at(-1));

    return Boolean(latestRecording);
  }

  async isInRecordingState({ id }: { id: Uuid }): Promise<boolean> {
    const recording = await this.getById({ id });
    return recording?.data.recording_status === AudioRecordingRecordingStatus.IN_PROGRESS;
  }
}
